import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { salesService } from "../services/service/salesService";

const EMPTY_PARAMS = {};
const SALES_KEY = ["sales"];
const MY_SHOPS_SALES_KEY = ["my-shops-sales"];

export const useSales = (initialParams = EMPTY_PARAMS) => {
    const queryClient = useQueryClient();
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [sellerFilter, setSellerFilter] = useState("");
    const [stockFilter, setStockFilter] = useState("");

    const shopId = initialParams.shopId;

    const params = useMemo(() => ({
        page: currentPage,
        per_page: pageSize,
        search: searchTerm || undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        sellerId: sellerFilter || undefined,
        stockId: stockFilter || undefined,
        ...initialParams,
    }), [currentPage, pageSize, searchTerm, statusFilter, startDate, endDate, sellerFilter, stockFilter, initialParams]);

    const query = useQuery({
    queryKey: [...SALES_KEY, params],
    queryFn: () => shopId
        ? salesService.getByShop(shopId, { ...params, flat: 1 })
        : salesService.getAll(params),
    staleTime: Infinity,
});

    const invalidate = useCallback(() =>
        queryClient.invalidateQueries({ queryKey: SALES_KEY }),
        [queryClient]
    );

    const createSale = useCallback(async (data) => {
        const response = await salesService.create(data);
        await invalidate();
        return response;
    }, [invalidate]);

    const createSales = useCallback(async (items) => {
        const responses = await Promise.all(items.map((item) => salesService.create(item)));
        await Promise.all([
            invalidate(),
            queryClient.invalidateQueries({ queryKey: ["stocks"] }),
            queryClient.invalidateQueries({ queryKey: ["sale-available-stock"] }),
        ]);
        return responses;
    }, [invalidate, queryClient]);

    const updateSale = useCallback(async (id, data) => {
        const response = await salesService.update(id, data);
        await invalidate();
        return response;
    }, [invalidate]);

    const toggleSaleStatus = useCallback(async (id) => {
        const response = await salesService.toggleStatus(id);
        await invalidate();
        return response;
    }, [invalidate]);

    const deleteSale = useCallback(async (id) => {
        const response = await salesService.delete(id);
        await invalidate();
        return response;
    }, [invalidate]);

    const bulkUpdateStatus = useCallback(async (data) => {
        const response = await salesService.bulkUpdateStatus(data);
        await invalidate();
        return response;
    }, [invalidate]);

    const getStats = useCallback(async () => {
        if (shopId) {
            const response = await salesService.getStatsByShop(shopId, {
                sellerId: sellerFilter || undefined,
                stockId: stockFilter || undefined,
                start_date: startDate || undefined,
                end_date: endDate || undefined,
            });
            const data = response.data?.data || response.data || {};
            return {
                total_sales: data.total_sales || 0,
                total_revenue: data.total_revenue || 0,
                total_profit: data.total_profit || 0,
                total_loss: data.total_loss || 0,
                total_items_sold: data.total_items_sold || 0,
                total_items_unsold: data.total_items_unsold || 0,
                completed: data.status_breakdown?.completed || 0,
                pending: data.status_breakdown?.pending || 0,
                cancelled: data.status_breakdown?.cancelled || 0,
                active: data.status_breakdown?.active || 0,
                avg_profit_margin: data.total_revenue > 0
                    ? ((data.total_profit || 0) / data.total_revenue) * 100
                    : 0,
            };
        }

        const response = await salesService.getAll({ per_page: 1000 });
        const sales = response.data?.data || [];
        return await salesService.getStatsFromData(sales);
    }, [shopId, sellerFilter, stockFilter, startDate, endDate]);

    return {
        sales: query.data?.data || [],
        pagination: query.data?.meta
            ? {
                  total: query.data.meta.total,
                  last_page: query.data.meta.last_page,
                  current_page: query.data.meta.current_page,
                  per_page: query.data.meta.per_page,
              }
            : null,
        loading: query.isLoading || query.isFetching,
        error: query.error,
        searchTerm,
        setSearchTerm,
        statusFilter,
        setStatusFilter,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        sellerFilter,
        setSellerFilter,
        stockFilter,
        setStockFilter,
        refresh: query.refetch,
        createSale,
        createSales,
        updateSale,
        toggleSaleStatus,
        deleteSale,
        bulkUpdateStatus,
        getStats
    };
};

export const useMyShopsSales = (initialParams = EMPTY_PARAMS) => {
    const queryClient = useQueryClient();
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [shopFilter, setShopFilter] = useState("");
    const [sellerFilter, setSellerFilter] = useState("");
    const [stockFilter, setStockFilter] = useState("");

    const params = useMemo(() => ({
        shopId: shopFilter || undefined,
        page: currentPage,
        per_page: pageSize,
        search: searchTerm || undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        sellerId: sellerFilter || undefined,
        stockId: stockFilter || undefined,
        ...initialParams,
    }), [
        shopFilter,
        currentPage,
        pageSize,
        searchTerm,
        statusFilter,
        startDate,
        endDate,
        sellerFilter,
        stockFilter,
        initialParams,
    ]);

    const query = useQuery({
    queryKey: [...MY_SHOPS_SALES_KEY, params],
    queryFn: () => salesService.getMyShops({ ...params, flat: 1 }),
    staleTime: Infinity,
});

    const invalidate = useCallback(() =>
        queryClient.invalidateQueries({ queryKey: MY_SHOPS_SALES_KEY }),
        [queryClient]
    );

    const createSale = useCallback(async (data) => {
        const response = await salesService.create(data);
        await Promise.all([
            invalidate(),
            queryClient.invalidateQueries({ queryKey: ["stocks"] }),
            queryClient.invalidateQueries({ queryKey: ["my-shops-stocks"] }),
            queryClient.invalidateQueries({ queryKey: ["sale-available-stock"] }),
            queryClient.invalidateQueries({ queryKey: ["my-shops-available-stock"] }),
        ]);
        return response;
    }, [invalidate, queryClient]);

    const createSales = useCallback(async (items) => {
        const responses = await Promise.all(items.map((item) => salesService.create(item)));
        await Promise.all([
            invalidate(),
            queryClient.invalidateQueries({ queryKey: ["stocks"] }),
            queryClient.invalidateQueries({ queryKey: ["my-shops-stocks"] }),
            queryClient.invalidateQueries({ queryKey: ["sale-available-stock"] }),
            queryClient.invalidateQueries({ queryKey: ["my-shops-available-stock"] }),
        ]);
        return responses;
    }, [invalidate, queryClient]);

    const updateSale = useCallback(async (id, data) => {
        const response = await salesService.update(id, data);
        await invalidate();
        return response;
    }, [invalidate]);

    const toggleSaleStatus = useCallback(async (id) => {
        const response = await salesService.toggleStatus(id);
        await invalidate();
        return response;
    }, [invalidate]);

    const deleteSale = useCallback(async (id) => {
        const response = await salesService.delete(id);
        await invalidate();
        return response;
    }, [invalidate]);

    const bulkUpdateStatus = useCallback(async (data) => {
        const response = await salesService.bulkUpdateStatus(data);
        await invalidate();
        return response;
    }, [invalidate]);

    return {
        sales: query.data?.data || [],
        pagination: query.data?.meta
            ? {
                  total: query.data.meta.total,
                  last_page: query.data.meta.last_page,
                  current_page: query.data.meta.current_page,
                  per_page: query.data.meta.per_page,
              }
            : null,
        loading: query.isLoading || query.isFetching,
        error: query.error,
        searchTerm,
        setSearchTerm,
        statusFilter,
        setStatusFilter,
        shopFilter,
        setShopFilter,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        sellerFilter,
        setSellerFilter,
        stockFilter,
        setStockFilter,
        refresh: query.refetch,
        createSale,
        createSales,
        updateSale,
        toggleSaleStatus,
        deleteSale,
        bulkUpdateStatus,
    };
};

export const useSalesBySeller = (sellerId, initialParams = EMPTY_PARAMS) => {
    const queryClient = useQueryClient();
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [statusFilter, setStatusFilter] = useState("all");

    const params = useMemo(() => ({
        page: currentPage,
        per_page: pageSize,
        status: statusFilter !== "all" ? statusFilter : undefined,
        ...initialParams,
    }), [currentPage, pageSize, statusFilter, initialParams]);

    const query = useQuery({
        queryKey: [...SALES_KEY, "seller", sellerId, params],
        queryFn: () => salesService.getBySeller(sellerId, params),
        enabled: Boolean(sellerId),
        staleTime: Infinity,
    });

    const invalidate = useCallback(() =>
        queryClient.invalidateQueries({ queryKey: SALES_KEY }),
        [queryClient]
    );

    return {
        sales: query.data?.data || [],
        pagination: query.data?.meta
            ? {
                  total: query.data.meta.total,
                  last_page: query.data.meta.last_page,
                  current_page: query.data.meta.current_page,
                  per_page: query.data.meta.per_page,
              }
            : null,
        loading: query.isLoading || query.isFetching,
        error: query.error,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        statusFilter,
        setStatusFilter,
        refresh: query.refetch,
        invalidate
    };
};

export const useSalesByStock = (stockId, initialParams = EMPTY_PARAMS) => {
    const queryClient = useQueryClient();
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const params = useMemo(() => ({
        page: currentPage,
        per_page: pageSize,
        ...initialParams,
    }), [currentPage, pageSize, initialParams]);

    const query = useQuery({
        queryKey: [...SALES_KEY, "stock", stockId, params],
        queryFn: () => salesService.getByStock(stockId, params),
        enabled: Boolean(stockId),
        staleTime: Infinity,
    });

    const invalidate = useCallback(() =>
        queryClient.invalidateQueries({ queryKey: SALES_KEY }),
        [queryClient]
    );

    return {
        sales: query.data?.data || [],
        pagination: query.data?.meta
            ? {
                  total: query.data.meta.total,
                  last_page: query.data.meta.last_page,
                  current_page: query.data.meta.current_page,
                  per_page: query.data.meta.per_page,
              }
            : null,
        loading: query.isLoading || query.isFetching,
        error: query.error,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        refresh: query.refetch,
        invalidate
    };
};

export const useSalesStats = () => {
    const query = useQuery({
        queryKey: [...SALES_KEY, "stats"],
        queryFn: async () => {
            const response = await salesService.getAll({ per_page: 1000 });
            const sales = response.data?.data || [];
            return await salesService.getStatsFromData(sales);
        },
        staleTime: 300000,
    });

    return {
        stats: query.data || {
            total_sales: 0,
            total_revenue: 0,
            total_profit: 0,
            total_loss: 0,
            total_items_sold: 0,
            total_items_unsold: 0,
            completed: 0,
            pending: 0,
            cancelled: 0,
            active: 0,
            avg_profit_margin: 0
        },
        loading: query.isLoading,
        error: query.error,
        refresh: query.refetch
    };
};